/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/

import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageLayout from 'dashboard-template/examples/LayoutContainers/PageLayout';
import { AssetInterface } from 'medicalsystem-shared';
import { getFileCategory, isOfficeDocument } from 'utils/blobProxy.utils';
import { useAuthenticatedAsset } from 'hooks/useAuthenticatedAsset.hook';
import { OfficeDocumentRenderer } from 'components/generals/OfficeDocumentRenderer';
import DocViewer, { DocViewerRenderers } from 'react-doc-viewer';
import { canUseDocViewer } from 'utils/blobProxy.utils';
import { RoutesConstants } from 'medicalsystem-shared';

/**
 * Full screen asset viewer page for displaying documents, images, and media files.
 * 
 * Features:
 * - Full viewport display with no navigation distractions
 * - Supports all file types: PDFs, images, videos, audio, Office documents
 * - Uses authenticated blob proxy for secure file access
 * - Handles Office document rendering with OfficeDocumentRenderer
 * - Can be opened in a new browser tab/window
 * - Receives asset data via navigation state
 * 
 * Navigation:
 * - Asset data must be passed via location state when navigating to this page
 * - If no asset data found, shows error and redirects
 * 
 * Usage:
 * ```tsx
 * navigate(RoutesConstants.ASSET_VIEWER.replace(':assetId', asset.id), {
 *   state: { asset }
 * });
 * ```
 * 
 * @returns Full screen asset viewer
 */
const AssetViewerPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [asset, setAsset] = useState<AssetInterface | null>(null);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedAsset = useRef(false); // Prevent double execution in StrictMode

  // Get asset from location state
  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasLoadedAsset.current) {
      return;
    }
    
    // Try to get asset from location state first
    let assetFromState = location.state?.asset as AssetInterface;
    
    // If not in state, check for stateKey in URL params (for window.open scenarios)
    if (!assetFromState) {
      const params = new URLSearchParams(location.search);
      const stateKey = params.get('stateKey');
      
      if (stateKey) {
        // Use localStorage to match storage location
        const storedData = localStorage.getItem(stateKey);
        
        if (storedData) {
          try {
            const parsed = JSON.parse(storedData);
            assetFromState = parsed.asset;
            // Clean up after reading to avoid cluttering localStorage
            localStorage.removeItem(stateKey);
          } catch (err) {
            console.error('Failed to parse asset from sessionStorage', err);
          }
        }
      }
    }
    
    if (assetFromState) {
      setAsset(assetFromState);
      hasLoadedAsset.current = true; // Mark as loaded
    } else {
      setError(t('documentViewer.assetNotFound'));
      // Redirect back after showing error
      setTimeout(() => {
        navigate(-1);
      }, 2000);
    }
  }, [location.state, location.search, navigate, t]);

  const { blobUrl, isLoading, error: assetError, progress } = useAuthenticatedAsset(asset);

  if (error || !asset) {
    return (
      <PageLayout>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {error || t('documentViewer.assetNotFound')}
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  if (assetError) {
    return (
      <PageLayout>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            p: 3,
          }}
        >
          <Alert severity="error" sx={{ maxWidth: 600 }}>
            {assetError}
          </Alert>
        </Box>
      </PageLayout>
    );
  }

  if (isLoading || !blobUrl) {
    return (
      <PageLayout>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            gap: 2,
            px: 4,
          }}
        >
          <Box sx={{ position: 'relative', display: 'inline-flex' }}>
            <CircularProgress 
              variant="determinate" 
              value={progress}
              size={80}
              thickness={4}
              sx={{ color: 'primary.main' }}
            />
            <Box
              sx={{
                top: 0,
                left: 0,
                bottom: 0,
                right: 0,
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography 
                variant="h6" 
                component="div" 
                color="text.secondary"
              >
                {`${progress}%`}
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('documentViewer.loading')}
          </Typography>
        </Box>
      </PageLayout>
    );
  }

  const renderContent = () => {
    const category = getFileCategory(asset.mimetype);

    // Office documents
    if (isOfficeDocument(asset.mimetype)) {
      return (
        <OfficeDocumentRenderer
          originalUrl={blobUrl}
          filename={asset.orgName}
          mimetype={asset.mimetype}
        />
      );
    }

    // PDF
    if (category === 'pdf') {
      return (
        <iframe
          src={`${blobUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
          style={{
            width: '100%',
            height: '100vh',
            border: 'none',
          }}
          title={asset.orgName}
        />
      );
    }

    // Images
    if (category === 'image') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
            bgcolor: '#000',
          }}
        >
          <img
            src={blobUrl}
            alt={asset.orgName}
            style={{
              maxWidth: '100%',
              maxHeight: '100vh',
              objectFit: 'contain',
            }}
          />
        </Box>
      );
    }

    // Video
    if (category === 'video') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
            bgcolor: '#000',
          }}
        >
          <video
            controls
            src={blobUrl}
            style={{
              maxWidth: '100%',
              maxHeight: '100vh',
            }}
          >
            {t('documentViewer.videoNotSupported')}
          </video>
        </Box>
      );
    }

    // Audio
    if (category === 'audio') {
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            minHeight: '100vh',
          }}
        >
          <audio
            controls
            src={blobUrl}
            style={{
              width: '100%',
              maxWidth: '600px',
            }}
          >
            {t('documentViewer.audioNotSupported')}
          </audio>
        </Box>
      );
    }

    // Text/Document viewer for plain text, CSV, etc.
    if (canUseDocViewer(asset.mimetype)) {
      return (
        <Box sx={{ minHeight: '100vh', width: '100%' }}>
          <DocViewer
            documents={[{ uri: blobUrl }]}
            pluginRenderers={DocViewerRenderers}
            style={{ height: '100vh', width: '100%' }}
            config={{
              header: {
                disableHeader: false,
                disableFileName: false,
                retainURLParams: false,
              },
            }}
          />
        </Box>
      );
    }

    // Fallback for other file types
    return (
      <iframe
        src={blobUrl}
        style={{
          width: '100%',
          height: '100vh',
          border: 'none',
        }}
        title={asset.orgName}
      />
    );
  };

  return (
    <PageLayout>
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {renderContent()}
      </Box>
    </PageLayout>
  );
};

export default AssetViewerPage;
